import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, Circle, ArrowsClockwise, Calendar, TrendUp, TrendDown, MinusSquare, WarningCircle, Star, ChatCircleText } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diff < 10) return "just now";
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function GrowthBlock({ growth, accentClass, label }) {
  if (!growth) return null;
  const dir = growth.direction;
  const pct = growth.delta_pct;
  const pillTone = dir === "up"
    ? "bg-emerald-100 text-emerald-900 border-emerald-200"
    : dir === "down"
    ? "bg-rose-100 text-rose-900 border-rose-200"
    : "bg-stone-100 text-stone-700 border-stone-200";
  const verdictTone = dir === "up"
    ? "text-emerald-900"
    : dir === "down"
    ? "text-rose-900"
    : "text-stone-700";
  const Icon = dir === "up" ? TrendUp : dir === "down" ? TrendDown : MinusSquare;
  return (
    <div className={`mt-5 rounded-xl p-4 border ${pillTone} ${accentClass}`} data-testid={`growth-${label}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon size={20} weight="duotone" />
          <span className="font-serif-display text-2xl font-light">{pct >= 0 ? "+" : ""}{pct}%</span>
        </div>
        <div className="text-xs text-muted">
          <b className="font-medium">This window:</b> {growth.current} · <b className="font-medium">Previous:</b> {growth.previous}
        </div>
      </div>
      <p className={`text-sm mt-2.5 font-light leading-snug ${verdictTone}`}>{growth.verdict}</p>
    </div>
  );
}

export default function AdminActiveOnlinePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) { navigate("/"); }
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/active-online", { params: { window_minutes: 5 } });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load active online users");
    }
  }, []);

  // Low-rating (<= 2★) reviews insight — separate state because it costs an
  // LLM call per row and shouldn't be on the 10s realtime polling cycle.
  const [lowRatings, setLowRatings] = useState(null);
  const [lowRatingsLoading, setLowRatingsLoading] = useState(false);
  const [lowRatingsError, setLowRatingsError] = useState(null);
  const [customerLookup, setCustomerLookup] = useState("");
  const loadLowRatings = useCallback(async (lookup = "") => {
    setLowRatingsLoading(true);
    setLowRatingsError(null);
    try {
      const params = { limit: 25 };
      const v = (lookup || "").trim();
      if (v) {
        // Treat input with '@' as email, otherwise as name. Both filters get
        // a case-insensitive substring match server-side.
        if (v.includes("@")) params.email = v;
        else                  params.name = v;
      } else {
        params.days = 30;
        params.max_rating = 2;
      }
      const r = await api.get("/admin/low-rating-insights", { params });
      setLowRatings(r.data);
    } catch (e) {
      setLowRatingsError(e?.response?.data?.detail || "Failed to load low-rating insights");
    } finally {
      setLowRatingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_super_admin) return;
    load();
    loadLowRatings();
    const id = setInterval(() => {
      setPulse((p) => p + 1);
      load();
    }, 10_000);
    return () => clearInterval(id);
  }, [user, load, loadLowRatings]);
  if (authLoading || !user?.is_super_admin) {
    return <PageShell><Header /><div className="p-10 text-center text-muted">Loading…</div><Footer /></PageShell>;
  }

  return (
    <PageShell>
      <Header />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-8">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Users size={14} weight="duotone" /> Super-admin · realtime
          </div>
          <div className="flex items-baseline justify-between gap-4 flex-wrap mt-3">
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
              Active online <span className="text-gold">now</span>
            </h1>
            <button
              onClick={load}
              className="btn-ghost rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 hover:text-gold"
              data-testid="refresh-btn"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
          </div>
          <p className="text-muted text-sm mt-3 font-light">
            Auto-refreshes every 10 seconds. Window: last 5 minutes of activity.
          </p>
        </header>

        {error && (
          <div className="rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm mb-6" data-testid="online-error">{error}</div>
        )}

        {data && (
          <>
            {/* === LIVE COUNT (the only number that matters) === */}
            <section className="rounded-3xl overflow-hidden border border-emerald-200 bg-gradient-to-br from-[#0F2417] via-[#163C24] to-[#1F5132] text-emerald-50" data-testid="live-count-card">
              <div className="p-8 sm:p-12 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Circle size={10} weight="fill" className={`text-emerald-300 ${pulse % 2 ? "opacity-100" : "opacity-50"} transition-opacity`} />
                  <span className="text-[11px] font-bold tracking-[0.2em] text-emerald-300">LIVE · LAST 5 MIN</span>
                </div>
                <div className="font-serif-display text-7xl sm:text-8xl lg:text-9xl mt-5 font-light leading-none" data-testid="online-total">
                  {data.total_online}
                </div>
                <div className="text-sm text-emerald-200 mt-4 font-light">
                  {data.total_online === 1 ? "person" : "people"} active right now
                </div>
                <div className="mt-7 flex items-center justify-center gap-7 text-sm">
                  <div data-testid="online-signed-in">
                    <span className="font-serif-display text-3xl text-white">{data.signed_in_online}</span>
                    <span className="ml-2 text-emerald-200/80">signed in</span>
                  </div>
                  <div className="h-6 w-px bg-emerald-200/30" />
                  <div data-testid="online-anonymous">
                    <span className="font-serif-display text-3xl text-white">{data.anonymous_online}</span>
                    <span className="ml-2 text-emerald-200/80">anonymous</span>
                  </div>
                </div>
              </div>
            </section>

            {/* === SESSIONS TABLE === */}
            <section className="mt-8 rounded-2xl overflow-hidden border border-soft" data-testid="sessions-table">
              <div className="px-5 py-3 bg-[#FAF4E8] flex items-center justify-between text-xs text-muted">
                <div className="label-eyebrow">Sessions ({data.sessions.length})</div>
                <div className="font-light">As of {new Date(data.as_of).toLocaleTimeString()}</div>
              </div>
              {data.sessions.length === 0 ? (
                <div className="p-10 text-center text-muted text-sm" data-testid="empty-state">
                  No one is active in the last 5 minutes.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white text-xs text-muted">
                    <tr>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3 hidden sm:table-cell">Page</th>
                      <th className="text-left p-3 hidden md:table-cell">Device</th>
                      <th className="text-left p-3">Last seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-soft">
                    {data.sessions.map((s, i) => (
                      <tr key={s.visitor_id} className="hover:bg-amber-50/40" data-testid={`session-row-${i}`}>
                        <td className="p-3">
                          {s.signed_in ? (
                            <div>
                              <div className="font-medium text-[#3F2D1B]">{s.user_name || "(no name)"}</div>
                              <div className="text-xs text-muted">{s.user_email}</div>
                            </div>
                          ) : (
                            <div className="text-muted text-xs">Anonymous · {s.visitor_id.slice(0, 10)}…</div>
                          )}
                        </td>
                        <td className="p-3 text-muted hidden sm:table-cell">
                          <span className="font-mono text-xs">{s.last_path || "/"}</span>
                        </td>
                        <td className="p-3 text-muted text-xs hidden md:table-cell">{s.device}</td>
                        <td className="p-3 text-xs">{timeAgo(s.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* === DAILY ACTIVE USERS === */}
            {data.daily && (
              <section className="mt-10 rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200" data-testid="daily-section">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div>
                    <div className="label-eyebrow text-sky-900 flex items-center gap-2">
                      <Calendar size={14} weight="duotone" /> Past 24 hours · Daily Active Users
                    </div>
                    <div className="font-serif-display text-5xl sm:text-6xl font-light mt-3 text-[#3F2D1B]" data-testid="dau-number">
                      {data.daily.distinct_active}
                    </div>
                    <div className="text-sm text-muted mt-1">
                      <b className="text-[#3F2D1B]">{data.daily.distinct_signed_in}</b> signed-in · <b className="text-[#3F2D1B]">{data.daily.distinct_active - data.daily.distinct_signed_in}</b> anonymous
                    </div>
                  </div>
                </div>
                <GrowthBlock growth={data.daily.growth} label="day" />
                <div className="mt-6 h-56" data-testid="dau-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.daily.buckets} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#BAE6FD" vertical={false} />
                      <XAxis dataKey="label" stroke="#0369A1" tick={{ fontSize: 10 }} interval={2} />
                      <YAxis stroke="#0369A1" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "#E0F2FE", border: "1px solid #BAE6FD", borderRadius: 8 }}
                        labelFormatter={(label, payload) => {
                          const h = payload?.[0]?.payload?.hour;
                          return h ? new Date(h).toLocaleString() : `${label}:00`;
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="signed_in" stackId="a" fill="#0284C7" name="Signed in" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="anonymous" stackId="a" fill="#7DD3FC" name="Anonymous" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {/* === WEEKLY ACTIVE USERS === */}
            {data.weekly && (
              <section className="mt-6 rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200" data-testid="weekly-section">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div>
                    <div className="label-eyebrow text-amber-900 flex items-center gap-2">
                      <Calendar size={14} weight="duotone" /> Past 7 days · Weekly Active Users
                    </div>
                    <div className="font-serif-display text-5xl sm:text-6xl font-light mt-3 text-[#3F2D1B]" data-testid="wau-number">
                      {data.weekly.distinct_active}
                    </div>
                    <div className="text-sm text-muted mt-1">
                      <b className="text-[#3F2D1B]">{data.weekly.distinct_signed_in}</b> signed-in · <b className="text-[#3F2D1B]">{data.weekly.distinct_active - data.weekly.distinct_signed_in}</b> anonymous
                    </div>
                  </div>
                </div>
                <GrowthBlock growth={data.weekly.growth} label="week" />
                <div className="mt-6 h-56" data-testid="wau-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.weekly.buckets} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8DAB2" vertical={false} />
                      <XAxis dataKey="day" tickFormatter={(d) => new Date(d).toLocaleDateString("en", { weekday: "short" })} stroke="#7C6F4E" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#7C6F4E" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#FFF8E7", border: "1px solid #E8DAB2", borderRadius: 8 }} labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="signed_in" stackId="a" fill="#B8860B" name="Signed in" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="anonymous" stackId="a" fill="#E8C77E" name="Anonymous" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {/* === MONTHLY ACTIVE USERS === */}
            {data.monthly && (
              <section className="mt-6 rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200" data-testid="monthly-section">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div>
                    <div className="label-eyebrow text-indigo-900 flex items-center gap-2">
                      <Calendar size={14} weight="duotone" /> Past 30 days · Monthly Active Users
                    </div>
                    <div className="font-serif-display text-5xl sm:text-6xl font-light mt-3 text-[#3F2D1B]" data-testid="mau-number">
                      {data.monthly.distinct_active}
                    </div>
                    <div className="text-sm text-muted mt-1">
                      <b className="text-[#3F2D1B]">{data.monthly.distinct_signed_in}</b> signed-in · <b className="text-[#3F2D1B]">{data.monthly.distinct_active - data.monthly.distinct_signed_in}</b> anonymous
                    </div>
                  </div>
                </div>
                <GrowthBlock growth={data.monthly.growth} label="month" />
                <div className="mt-6 h-56" data-testid="mau-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.monthly.buckets} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#C7D2FE" vertical={false} />
                      <XAxis dataKey="day" tickFormatter={(d) => new Date(d).getDate()} stroke="#4338CA" tick={{ fontSize: 10 }} interval={2} />
                      <YAxis stroke="#4338CA" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 8 }} labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="signed_in" stackId="a" fill="#4F46E5" name="Signed in" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="anonymous" stackId="a" fill="#A5B4FC" name="Anonymous" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {/* ───────────── Low-rating (≤ 2★) review insights ───────────── */}
            <section className="mt-12 rounded-3xl border border-rose-200 bg-rose-50/40 p-5 sm:p-7" data-testid="low-rating-section">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <div className="label-eyebrow text-rose-900 flex items-center gap-2">
                    <WarningCircle size={14} weight="duotone" /> Reputation watch · {customerLookup.trim() ? `customer lookup` : `last 30 days`}
                  </div>
                  <h2 className="font-serif-display text-2xl sm:text-3xl mt-2 font-light leading-tight text-rose-950">
                    {customerLookup.trim() ? (
                      <>Investigating <span className="text-rose-700">"{customerLookup.trim()}"</span></>
                    ) : (
                      <>Signed-in customers who left <span className="text-rose-700">2★ or less</span></>
                    )}
                  </h2>
                  <p className="text-rose-900/80 text-sm mt-1 font-light max-w-2xl">
                    Each row names the astrologer consulted and the most likely reason for the bad experience — inferred from the actual chat transcript.
                  </p>
                </div>
                <button
                  onClick={() => loadLowRatings(customerLookup)}
                  className="btn-ghost rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 text-rose-800 hover:text-rose-950"
                  data-testid="low-rating-refresh-btn"
                >
                  <ArrowsClockwise size={14} weight="bold" /> Refresh
                </button>
              </div>

              {/* Per-customer lookup search box — type name or email and hit
                  enter to investigate any one customer's review history. */}
              <form
                onSubmit={(e) => { e.preventDefault(); loadLowRatings(customerLookup); }}
                className="mt-4 flex items-stretch gap-2 max-w-xl"
                data-testid="low-rating-search-form"
              >
                <input
                  type="text"
                  value={customerLookup}
                  onChange={(e) => setCustomerLookup(e.target.value)}
                  placeholder="Look up a customer by name or email (e.g. arunima or jane@x.com)"
                  className="flex-1 px-4 py-2.5 text-sm rounded-full bg-white border border-rose-200 outline-none focus:border-rose-400 placeholder:text-rose-900/40"
                  data-testid="low-rating-search-input"
                />
                <button
                  type="submit"
                  className="bg-rose-700 hover:bg-rose-800 text-white font-medium px-5 py-2.5 rounded-full text-sm transition-colors"
                  data-testid="low-rating-search-btn"
                >
                  Look up
                </button>
                {customerLookup.trim() && (
                  <button
                    type="button"
                    onClick={() => { setCustomerLookup(""); loadLowRatings(""); }}
                    className="text-rose-700 hover:text-rose-900 text-sm px-3"
                    data-testid="low-rating-search-clear"
                  >
                    Clear
                  </button>
                )}
              </form>

              {lowRatingsError && (
                <div className="mt-4 text-sm text-rose-700">{lowRatingsError}</div>
              )}
              {lowRatingsLoading && !lowRatings && (
                <div className="mt-6 text-sm text-rose-900/70">Analysing chats with AI…</div>
              )}
              {lowRatings && lowRatings.count === 0 && (
                <div className="mt-6 p-5 rounded-2xl bg-white border border-rose-200 text-rose-900/80 text-sm" data-testid="low-rating-empty">
                  {lowRatings.not_found
                    ? <>No customer matches "{customerLookup.trim()}". Try a different spelling, or paste their email.</>
                    : customerLookup.trim()
                    ? <>"{customerLookup.trim()}" exists but has not left any reviews.</>
                    : "✨ No 2★-or-less reviews from signed-in customers in the last 30 days. Reputation is clean."}
                </div>
              )}
              {lowRatings && lowRatings.count > 0 && (
                <div className="mt-5 space-y-4" data-testid="low-rating-list">
                  {lowRatings.reviews.map((rv) => (
                    <article
                      key={rv.review_id}
                      className="rounded-2xl bg-white border border-rose-200 p-4 sm:p-5"
                      data-testid={`low-rating-row-${rv.review_id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-900 text-xs font-medium">
                              <Star size={12} weight="fill" /> {rv.rating}/5
                            </span>
                            <span className="font-medium text-rose-950 truncate">{rv.customer_name}</span>
                            <span className="text-rose-900/55 text-xs truncate">· {rv.customer_email}</span>
                          </div>
                          <div className="text-rose-900/70 text-xs mt-1.5">
                            Consulted <strong className="text-rose-950">{rv.astrologer_name}</strong>
                            {rv.astrologer_specialization && (
                              <> · {rv.astrologer_specialization}{rv.astrologer_is_ai ? " (AI)" : ""}
                                {rv.astrologer_experience_years != null && <> · {rv.astrologer_experience_years}y</>}</>
                            )} · {rv.message_count} messages · {timeAgo(rv.created_at)}
                          </div>
                        </div>
                      </div>

                      {rv.comment && (
                        <div className="mt-3 p-3 rounded-lg bg-rose-50 border border-rose-100 text-sm text-rose-950 italic" data-testid={`low-rating-comment-${rv.review_id}`}>
                          "{rv.comment}"
                        </div>
                      )}

                      <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid={`low-rating-insight-${rv.review_id}`}>
                        <div className="label-eyebrow text-amber-900 flex items-center gap-1.5 mb-1.5">
                          <ChatCircleText size={12} weight="duotone" /> AI insight · likely reason
                        </div>
                        <p className="text-sm text-amber-950 leading-relaxed">{rv.likely_reason}</p>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
      <Footer />
    </PageShell>
  );
}
